import React, { useCallback } from 'react';
import { MoreVertical, Share2, Eye, EyeOff, AlertTriangle, Pencil, Pause, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import './AdActionsMenu.css';

/**
 * iter331 — AdActionsMenu
 *
 * Premium three-dot ⋮ button overlaid on the Ad Details hero image.
 * Replaces the visible Report / Hide buttons that previously sat
 * below the Safety Tips card. All existing handlers stay where they
 * were — this is purely a UI relocation per operator spec.
 *
 *   Non-owner viewer (default):
 *     • Share ad
 *     • Hide this ad
 *     • Report ad           ← only when `hasReported === false`
 *
 *   Owner viewer (own ad):
 *     • Share ad
 *     • Edit ad
 *     • Pause / Hide listing
 *     • Delete ad           ← destructive (red)
 *
 * The component is dumb on purpose: it only knows about labels and
 * which handler to call. Confirmation dialogs (hide, delete) and the
 * Report sheet remain owned by the page so we don't fork their state
 * here.
 *
 * Placement:
 *   The button is positioned absolute top-START on the hero gallery
 *   (heart already lives top-END). Using `inset-inline-start` keeps
 *   the menu opposite the heart in both LTR and RTL.
 */
export default function AdActionsMenu({
  isOwner = false,
  isArabic = false,
  hasReported = false,
  onShare,
  onHide,           // non-owner "hide from feed"
  onReport,
  onEdit,
  onPause,          // owner "pause / hide listing"
  onDelete,
  testid = 'ad-actions-menu',
}) {
  const stop = useCallback((e) => {
    // Prevent the underlying gallery click from firing when the user
    // taps the menu button or a menu item.
    e.stopPropagation();
  }, []);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ad-actions-menu__trigger"
          onClick={stop}
          data-testid={`${testid}-trigger`}
          aria-label={isArabic ? 'إجراءات الإعلان' : 'Ad actions'}
        >
          <MoreVertical size={20} strokeWidth={2.25} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={isArabic ? 'start' : 'end'}
        sideOffset={8}
        className="ad-actions-menu__content"
        dir={isArabic ? 'rtl' : 'ltr'}
        data-testid={`${testid}-content`}
      >
        {/* Share — visible to everyone */}
        <DropdownMenuItem
          onClick={(e) => { stop(e); onShare?.(); }}
          className="ad-actions-menu__item"
          data-testid={`${testid}-share`}
        >
          <Share2 size={16} className="ad-actions-menu__icon" />
          <span>{isArabic ? 'مشاركة الإعلان' : 'Share ad'}</span>
        </DropdownMenuItem>

        {isOwner ? (
          <>
            <DropdownMenuItem
              onClick={(e) => { stop(e); onEdit?.(); }}
              className="ad-actions-menu__item"
              data-testid={`${testid}-edit`}
            >
              <Pencil size={16} className="ad-actions-menu__icon" />
              <span>{isArabic ? 'تعديل الإعلان' : 'Edit ad'}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => { stop(e); onPause?.(); }}
              className="ad-actions-menu__item"
              data-testid={`${testid}-pause`}
            >
              <Pause size={16} className="ad-actions-menu__icon" />
              <span>{isArabic ? 'إيقاف الإعلان مؤقتاً' : 'Pause listing'}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => { stop(e); onDelete?.(); }}
              className="ad-actions-menu__item ad-actions-menu__item--danger"
              data-testid={`${testid}-delete`}
            >
              <Trash2 size={16} className="ad-actions-menu__icon" />
              <span>{isArabic ? 'حذف الإعلان' : 'Delete ad'}</span>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem
              onClick={(e) => { stop(e); onHide?.(); }}
              className="ad-actions-menu__item"
              data-testid={`${testid}-hide`}
            >
              <EyeOff size={16} className="ad-actions-menu__icon" />
              <span>{isArabic ? 'إخفاء هذا الإعلان' : 'Hide this ad'}</span>
            </DropdownMenuItem>
            {hasReported ? (
              <DropdownMenuItem
                disabled
                className="ad-actions-menu__item ad-actions-menu__item--disabled"
                data-testid={`${testid}-reported`}
              >
                <Eye size={16} className="ad-actions-menu__icon" />
                <span>{isArabic ? 'تم الإبلاغ' : 'Already reported'}</span>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={(e) => { stop(e); onReport?.(); }}
                className="ad-actions-menu__item ad-actions-menu__item--danger"
                data-testid={`${testid}-report`}
              >
                <AlertTriangle size={16} className="ad-actions-menu__icon" />
                <span>{isArabic ? 'الإبلاغ عن الإعلان' : 'Report ad'}</span>
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
